import React from 'react';
import { Box, Grid, Paper, Typography, Button, Chip, Stack } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ClassIcon from '@mui/icons-material/Class';
import SportsKabaddiIcon from '@mui/icons-material/SportsKabaddi';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import AppsIcon from '@mui/icons-material/Apps';
import LoginOutlinedIcon from '@mui/icons-material/LoginOutlined';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import QrCode2RoundedIcon from '@mui/icons-material/QrCode2Rounded';
import { useNavigate } from 'react-router-dom';

export default function Home({ token, user }) {
  const theme = useTheme();
  const custom = theme.palette.custom || {};
  const navigate = useNavigate();

  const professorQuickLinks = [
    {
      title: 'Mes classes',
      subtitle: "Gérer les élèves et les classes",
      icon: <ClassIcon sx={{ fontSize: 36, color: custom.primaryColor }} />,
      bg: custom.surfaceLight,
      to: '/classes'
    },
    {
      title: 'Activités & Compétences',
      subtitle: "Définir activités, compétences et barèmes",
      icon: <SportsKabaddiIcon sx={{ fontSize: 36, color: custom.primaryColor }} />,
      bg: custom.exportBg,
      to: '/activites'
    },
    {
      title: 'Suivi des élèves',
      subtitle: "Consulter et noter les acquis",
      icon: <PeopleAltIcon sx={{ fontSize: 36, color: custom.primaryColor }} />,
      bg: custom.printBg,
      to: '/suivi-eleves'
    },
    {
      title: 'Applications',
      subtitle: "Ouvrir JugeBoxe, Carnet Muscu, ...",
      icon: <AppsIcon sx={{ fontSize: 36, color: custom.primaryColor }} />,
      bg: custom.primaryBg,
      to: '/apps'
    }
  ];

  const universes = [
    {
      title: 'Espace Prof',
      subtitle: 'Gestion des classes, des compétences et du suivi pédagogique.',
      description: 'Retrouve tes parcours de gestion dans un espace lisible, pensé pour préparer et piloter tes séances.',
      icon: <ClassIcon sx={{ fontSize: 40, color: custom.primaryColor }} />,
      to: '/prof',
      action: 'Entrer dans l’espace prof',
      accent: '#EFF4FF',
      badge: 'Réservé prof'
    },
    {
      title: 'Applications Élèves',
      subtitle: 'Applications terrain et accès QR pour les élèves.',
      description: 'Centralise les outils opérationnels, les parcours tactiles et les entrées directes depuis les QR personnels.',
      icon: <QrCode2RoundedIcon sx={{ fontSize: 40, color: custom.primaryColor }} />,
      to: '/apps',
      action: 'Ouvrir les applications',
      accent: '#F5F1FF',
      badge: 'QR & usage mobile'
    }
  ];

  const handleCardClick = (to) => {
    if (!to) return;
    navigate(to);
  };

  const greeting = user ? (user.prenom || user.nom ? `Bonjour, ${((user.prenom || '') + ' ' + (user.nom || '')).trim()}` : `Bonjour, ${user.email || 'Utilisateur'}`) : null;

  const cardSx = (bg) => ({
    p: 3,
    minHeight: 160,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: 1,
    borderRadius: 2,
    backgroundColor: bg || '#fff',
    transition: 'transform 220ms ease, box-shadow 220ms ease',
    cursor: 'pointer',
    boxShadow: '0 1px 6px rgba(15,15,15,0.04)',
    '&:hover': {
      transform: 'translateY(-6px) scale(1.02)',
      boxShadow: '0 10px 30px rgba(32,24,64,0.12)'
    }
  });

  // Disconnected hero styles
  const heroSx = {
    minHeight: { xs: 420, sm: 480 },
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    bgcolor: '#fbfdff',
    borderRadius: 3,
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.6)',
    mb: 4,
    px: 2
  };

  const logoStyle = {
    fontFamily: `"Montserrat", "Segoe UI", Roboto, "Helvetica Neue", Arial`,
    fontWeight: 800,
    letterSpacing: 1,
    lineHeight: 1,
  };

  return (
    <Box sx={{ px: { xs: 2, sm: 4 }, py: { xs: 4, sm: 6 }, maxWidth: 1200, mx: 'auto' }}>
      {/* If not logged, show centered hero with title and subtitle */}
      {!user ? (
        <Paper sx={heroSx} elevation={0}>
          <Box sx={{ textAlign: 'center', maxWidth: 920 }}>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: 40, sm: 56, md: 72 },
                color: 'text.primary',
                mb: 2,
                ...logoStyle
              }}
            >
              My'EPS
            </Typography>

            <Typography variant="h6" sx={{ color: 'text.secondary', mb: 4 }}>
              Enseignez autrement... L'outil numérique au service des apprentissages...
            </Typography>

            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<LoginOutlinedIcon />}
                onClick={() => navigate('/login')}
                sx={{ textTransform: 'none', px: 4 }}
              >
                Connexion
              </Button>

              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/register')}
                sx={{ textTransform: 'none', px: 4 }}
              >
                Créer un compte
              </Button>
            </Box>
          </Box>
        </Paper>
      ) : (
        <>
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              {greeting}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Deux univers clairs : ton espace de gestion prof et l’espace applications dédié aux usages terrain et QR.
            </Typography>
          </Box>

          <Grid container spacing={3} sx={{ mb: 3 }}>
            {universes.map((c) => (
              <Grid key={c.title} item xs={12} md={6}>
                <Paper
                  role="button"
                  onClick={() => handleCardClick(c.to)}
                  sx={cardSx(c.accent)}
                  elevation={0}
                  aria-label={c.title}
                >
                  <Stack spacing={2} sx={{ height: '100%' }}>
                    <Chip label={c.badge} sx={{ alignSelf: 'flex-start', fontWeight: 700 }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{
                        width: 72, height: 72, borderRadius: 3,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        backgroundColor: 'rgba(255,255,255,0.7)'
                      }}>
                        {c.icon}
                      </Box>
                      <Box>
                        <Typography variant="h5" sx={{ fontWeight: 800 }}>{c.title}</Typography>
                        <Typography variant="body1" color="text.secondary">{c.subtitle}</Typography>
                      </Box>
                    </Box>
                    <Typography color="text.secondary">
                      {c.description}
                    </Typography>
                    <Box sx={{ flexGrow: 1 }} />
                    <Button
                      variant="contained"
                      endIcon={<ArrowForwardRoundedIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(c.to);
                      }}
                      sx={{ alignSelf: 'flex-start', minHeight: 48, px: 3 }}
                    >
                      {c.action}
                    </Button>
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, mb: 3 }} elevation={0}>
            <Typography variant="h6" sx={{ fontWeight: 800, mb: 1.5 }}>
              Raccourcis prof
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              Accès direct aux tâches les plus fréquentes depuis le tableau de bord.
            </Typography>
            <Grid container spacing={2}>
              {professorQuickLinks.map((c) => (
                <Grid key={c.title} item xs={12} sm={6} md={3}>
                  <Paper
                    role="button"
                    onClick={() => handleCardClick(c.to)}
                    sx={{
                      ...cardSx(c.bg),
                      minHeight: 140,
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 10px 24px rgba(32,24,64,0.10)'
                      }
                    }}
                    elevation={0}
                    aria-label={c.title}
                  >
                    <Box sx={{
                      width: 64, height: 64, borderRadius: 2,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      backgroundColor: 'rgba(255,255,255,0.6)'
                    }}>
                      {c.icon}
                    </Box>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>{c.title}</Typography>
                      <Typography variant="body2" color="text.secondary">{c.subtitle}</Typography>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </>
      )}
    </Box>
  );
}